<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import { getTransactionsAnalytics } from 'src/components/cabinet/#services/transactionsAnalytics';
  import Table from '../Table.svelte';
  import { options } from '../../configs/BarOptions';
  import { squezzeBranches } from '../../utils/branches';
  import { getMathRewardsAnalytics } from '../../../#services/mathRewardsAnalytics';
  import moment from 'moment';
  import { requestsParams } from '../../configs/Requests';
  import { colors, processChartDataset, resetChart } from '../../utils/common';
  import DateRange from '../common/DateRange.svelte';
  import GameSelect from '../common/GameSelect.svelte';

  let chart;

  let spinsRewardsBranch;
  let woodenLootboxesBranch;
  let tasksBranch;
  let achievementsBranch;
  let tournamentBranch;
  let bingoJackpotBranch;
  let bingoRewardBranch;
  let sumLisBranch;

  let creator = 'all';
  let country = null;
  let userId = null;

  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2022-12-30';
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    zoomPlugin,
  );

  function refreshLis() {
    const requestData = requestsParams.free.hard({
      country: country?.value,
      creator: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getTransactionsAnalytics.send({ data: requestData });
  }

  function refreshMathRewards() {
    const requestData = requestsParams.free.rewards({
      game: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getMathRewardsAnalytics.send({ data: requestData });
  }
  
  function refresh() {
    resetChart(chart, labels, datasets);
    refreshLis();
    refreshMathRewards();
  }

  getTransactionsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      value.value.branches[0].values.forEach(e => {
          labels.push(e.point)
      })

      processChartDataset(value.value, labels, datasets, false, 2);

      try {
        achievementsBranch = value.value.branches[0]?.values.map((v) => ({ point: v.point, amount: Math.abs(v.amount) }));
        tasksBranch = value.value.branches[1]?.values.map((v) => ({ point: v.point, amount: Math.abs(v.amount) }));
        bingoJackpotBranch = value.value.branches[2]?.values.map((v) => ({ point: v.point, amount: Math.abs(v.amount) }));
        bingoRewardBranch = value.value.branches[3]?.values.map((v) => ({ point: v.point, amount: Math.abs(v.amount) }));
        tournamentBranch = value.value.branches[4]?.values.map((v) => ({ point: v.point, amount: Math.abs(v.amount) }));
        sumLisBranch = squezzeBranches([
          achievementsBranch,
          tasksBranch,
          bingoJackpotBranch,
          bingoRewardBranch,
          tournamentBranch,
        ]);
      } catch (e) {}
    }
  });

  getMathRewardsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      datasets.unshift({
        label: 'Wooden lootboxes, LIS',
        backgroundColor: colors[1],
        borderColor: colors[1],
        fill: false,
        data: value.value.branches[1]?.values.map((v) => v.amount),
        stack: 0
      });

      datasets.unshift({
        label: 'Free spins, LIS',
        backgroundColor: colors[0],
        borderColor: colors[0],
        fill: false,
        data: value.value.branches[0]?.values.map((v) => v.amount),
        stack: 0
      });

      try {
        spinsRewardsBranch = value?.value?.branches[0]?.values;
        woodenLootboxesBranch = value.value.branches[1]?.values;
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
    getTransactionsAnalytics.reset();
    getMathRewardsAnalytics.reset();
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <GameSelect bind:creator={creator} refreshCallback={() => refresh()} />
  </div>
  <div class="pc-line">
    <span class="date-label">User</span>
    <input type="text" class="form-control" bind:value={userId} placeholder="Input UserID" on:input={refresh}>
  </div>
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
      <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<p class="description">Rewards that users can earn for free. Spins value derived analytically.</p>
<div class="graphic">
  {#if $getTransactionsAnalytics.error || $getMathRewardsAnalytics.error}
    {$getTransactionsAnalytics.error || $getMathRewardsAnalytics.error}
  {:else if $getTransactionsAnalytics.loading || $getMathRewardsAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if $getTransactionsAnalytics.value && $getMathRewardsAnalytics.value}
    <Chart bind:this={chart} type="bar" {data} width={500} height={270} {options} />
    <Table branches={{
      '📈 Free spins, LIS': spinsRewardsBranch,
      '📈 Wooden lootboxes, LIS': woodenLootboxesBranch,
      'Achievements, LIS': achievementsBranch,
      'Daily quests, LIS': tasksBranch,
      'Bingo jackpot, LIS': bingoJackpotBranch,
      'Bingo rewards, LIS': bingoRewardBranch,
      'Tournaments, LIS': tournamentBranch,
      'Total, LIS': squezzeBranches([sumLisBranch, spinsRewardsBranch, woodenLootboxesBranch]),
    }} isTableHidden={false} percentsInTotal={true} borders={[1,3,5]} />
  {/if}
</div>